import { createAvatar } from "@dicebear/core";
import { initials, rings, shapes } from "@dicebear/collection";
import { generateColorsFromSeed } from "./colorUtils";

/** Legacy worker/agent avatar — gradient + bolt icon overlay. */
export const WORKER_AVATAR_BOLT_DESIGN = "bolt" as const;

/** Production worker/agent avatar — seed gradient + Dicebear Waves overlay. */
export const WORKER_AVATAR_DEFAULT_DESIGN = "waves" as const;

/** Dicebear preview styles — compare in Codex Agents tab. */
export const WORKER_AVATAR_DICEBEAR_DESIGNS = [
  "weave",
  "waves",
  "landscape",
  "stripes",
  "shapes",
  "rings",
  "pixelbot",
  "initials",
] as const;

export type WorkerAvatarDicebearDesign = (typeof WORKER_AVATAR_DICEBEAR_DESIGNS)[number];

export type WorkerAvatarDesign = typeof WORKER_AVATAR_BOLT_DESIGN | WorkerAvatarDicebearDesign;

const DICEBEAR_API_STYLES: Set<WorkerAvatarDicebearDesign> = new Set([
  "weave",
  "waves",
  "landscape",
  "stripes",
  "pixelbot",
]);

/** Transparent background so Dicebear overlays our seed gradient in `WorkerAvatar`. */
const DICEBEAR_TRANSPARENT_BACKGROUND = "00000000";

export function hslColorStringToHex(hsl: string): string {
  const match = hsl.match(/hsl\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%\s*\)/i);
  if (!match) return "#6366f1";

  const h = Number(match[1]) / 360;
  const s = Number(match[2]) / 100;
  const l = Number(match[3]) / 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };

  let r: number;
  let g: number;
  let b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (channel: number) =>
    Math.round(channel * 255)
      .toString(16)
      .padStart(2, "0");

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Background palette from the same seed algorithm as `WorkerAvatar` gradients. */
export function workerAvatarBackgroundHexes(seed: string): string[] {
  const { color1, color2 } = generateColorsFromSeed(seed || "default");
  return [
    hslColorStringToHex(color1).replace("#", ""),
    hslColorStringToHex(color2).replace("#", ""),
  ];
}

function dicebearApiUrl(
  style: WorkerAvatarDicebearDesign,
  seed: string,
  size: number,
): string {
  const params = new URLSearchParams({
    seed,
    size: String(size),
    radius: "50",
  });
  params.append("backgroundColor", DICEBEAR_TRANSPARENT_BACKGROUND);
  return `https://api.dicebear.com/10.x/${style}/svg?${params.toString()}`;
}

/** Data URI or API URL for a Dicebear worker avatar preview overlay. */
export function getWorkerAvatarDicebearSrc(
  seed: string,
  design: WorkerAvatarDicebearDesign,
  size: number,
): string {
  const safeSeed = seed || "default";

  if (DICEBEAR_API_STYLES.has(design)) {
    return dicebearApiUrl(design, safeSeed, size);
  }

  const common = {
    seed: safeSeed,
    size,
    backgroundColor: [DICEBEAR_TRANSPARENT_BACKGROUND],
    radius: 50,
  };

  if (design === "shapes") {
    return createAvatar(shapes, common).toDataUri();
  }
  if (design === "rings") {
    return createAvatar(rings, common).toDataUri();
  }
  return createAvatar(initials, {
    ...common,
    scale: 80,
    fontWeight: 600,
    fontFamily: ["system-ui", "sans-serif"],
  }).toDataUri();
}

export function isWorkerAvatarDicebearDesign(value: string): value is WorkerAvatarDicebearDesign {
  return (WORKER_AVATAR_DICEBEAR_DESIGNS as readonly string[]).includes(value);
}
